import React, { useEffect, useRef, useState } from 'react';
import { FileText, Phone, MessageSquare } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PHONE_DISPLAY, PHONE_TEL } from '../constants';
import { useEnquirySmsHref } from '../lib/enquiry';
import { trackEvent } from '../analytics';
import { getScrollBehavior } from '../lib/motion';
import './StickyBar.css';

const scrollToContact = (e) => {
  const el = document.getElementById('contact');
  if (el) { e.preventDefault(); el.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' }); }
};

// Floating mobile contact dock. Publishes its height and safe-area offset so
// the footer clears it, and becomes inert whenever the enquiry form is visible.
const StickyBar = () => {
  const barRef = useRef(null);
  const navigate = useNavigate();
  const smsHref = useEnquirySmsHref();
  const [hidden, setHidden] = useState(false);
  const [contactVisible, setContactVisible] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    const contact = document.getElementById('contact');
    if (!contact || !window.IntersectionObserver) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      setContactVisible(entry.isIntersecting);
    }, {threshold: 0});
    observer.observe(contact);
    return () => observer.disconnect();
  }, []);

  // Hide on scroll-down past 80px, return on any scroll-up (200ms CSS slide).
  useEffect(() => {
    lastY.current = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const goingDown = y > lastY.current;
        setHidden(y > 80 && goingDown);
        lastY.current = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    const publish = () => {
      const h = el.offsetHeight || 0;
      const bottomInset = getComputedStyle(el).bottom;
      const bottomPx = parseFloat(bottomInset) || 0;
      const clearance = h > 0 ? Math.round(h + bottomPx) : 0;
      document.documentElement.style.setProperty('--sticky-bar-clearance', `${clearance}px`);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    window.addEventListener('resize', publish);
    window.addEventListener('orientationchange', publish);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', publish);
      window.removeEventListener('orientationchange', publish);
      document.documentElement.style.removeProperty('--sticky-bar-clearance');
    };
  }, []);

  return (
    <div ref={barRef} className={`sticky-bar sticky-bar--slim${hidden || contactVisible ? ' sticky-bar--hidden' : ''}`} inert={hidden || contactVisible} aria-hidden={hidden || contactVisible ? true : undefined} data-testid="sticky-bar" role="region" aria-label="Contact FairFix">
      <a
        href={smsHref}
        className="sticky-btn"
        data-testid="sticky-text-btn"
        aria-label="Send a text message"
        onClick={() => {
          trackEvent('contact_text', { location: 'sticky' });
          trackEvent('sms_click', { location: 'sticky' });
        }}
      >
        <MessageSquare size={18} strokeWidth={1.6} aria-hidden="true" />
        <span className="sticky-btn-label">Text</span>
      </a>
      <a
        href="#contact"
        className="sticky-btn sticky-btn--primary"
        data-testid="sticky-quote-btn"
        aria-label="Get a free quote"
        onClick={(e) => {
          trackEvent('contact_quote', { location: 'sticky' });
          trackEvent('quote_cta_click', { location: 'sticky' });
          // B02: the CURRENT page's form first (href is the local anchor, so
          // typed text/service survive) — only the 404 (no form) goes Home.
          const el = document.getElementById('contact');
          if (el) { scrollToContact(e); return; }
          e.preventDefault();
          navigate('/');
          setTimeout(() => {
            const t = document.getElementById('contact');
            if (t) t.scrollIntoView({ behavior: getScrollBehavior(), block: 'start' });
          }, 220);
        }}
      >
        <FileText size={18} strokeWidth={1.6} aria-hidden="true" />
        <span className="sticky-btn-label">Quote</span>
      </a>
      <a
        href={PHONE_TEL}
        className="sticky-btn"
        data-testid="sticky-call-btn"
        aria-label={`Call Corey — ${PHONE_DISPLAY}`}
        onClick={() => {
          trackEvent('contact_call', { location: 'sticky' });
          trackEvent('call_click', { location: 'sticky' });
        }}
      >
        <Phone size={18} strokeWidth={1.6} aria-hidden="true" />
        <span className="sticky-btn-label">Call</span>
      </a>
    </div>
  );
};

export default StickyBar;
